import { computed, makeObservable } from 'mobx'
import { dependsOn, getValue, trackedAction } from 'mobx-async'
import { computedFn } from 'mobx-utils'

import { BackOfficePrivilegeName } from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { arriere } from '@arriere/index'

type UserPermissions = {
  userPermissions?: BackOfficePrivilegeName[]
  voyageId?: string
  roles?: { id: string; name: string }[]
}

class _AuthStore {
  private _cachedUserPermissions: UserPermissions | undefined

  constructor() {
    makeObservable(this, {
      __ASYNC_userPermissions: computed({ keepAlive: true }),
      voyageUserId: computed,
      userPermissions: computed,
      userRoles: computed,
    })

    this.refreshUserPermission = trackedAction<any>(this.refreshUserPermission.bind(this))
  }

  get __ASYNC_userPermissions(): Promise<UserPermissions | undefined> {
    dependsOn(this.refreshUserPermission)

    if (this._cachedUserPermissions) {
      return Promise.resolve(this._cachedUserPermissions)
    }

    return arriere.auth.getUserPermissions().then(data => {
      this._cachedUserPermissions = data
      return data
    })
  }

  get voyageUserId() {
    return getValue(this.__ASYNC_userPermissions)?.voyageId
  }

  get userPermissions() {
    return getValue(this.__ASYNC_userPermissions)?.userPermissions
  }

  get userRoles() {
    return getValue(this.__ASYNC_userPermissions)?.roles
  }

  hasUserPermission = computedFn((privilegeName: BackOfficePrivilegeName): boolean => {
    return this.userPermissions?.includes(privilegeName) ?? false
  })

  hasUserRole = computedFn((roleId: string): boolean => {
    return this.userRoles?.some(role => role.id === roleId) ?? false
  })

  hasUserPermissions = computedFn(
    (
      privilegeNames: BackOfficePrivilegeName[],
      quantifier: 'some' | 'every' = 'every',
    ): boolean => {
      if (!this.userPermissions) return false
      return quantifier === 'some'
        ? privilegeNames.some(value => this.hasUserPermission(value))
        : privilegeNames.every(value => this.hasUserPermission(value))
    },
  )

  /**
   * Used to trigger a re-fetch of user permissions.
   */
  async refreshUserPermission() {
    this._cachedUserPermissions = undefined
    return Promise.resolve()
  }
}

export const AuthStore = new _AuthStore()
